import { useState, useEffect } from "react";
import Product from "./data/products.json";
import { Tooltip } from "@material-tailwind/react";
import axios from "axios";
import {
  InformationCircleIcon,
  MagnifyingGlassIcon,
  CubeIcon,
  HomeIcon,
  HomeModernIcon,
  UserIcon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  CheckIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/solid";
import {
  ArchiveBoxIcon,
  HomeIcon as HomeOutlineIcon,
  FireIcon,
  MoonIcon,
  SparklesIcon,
  ComputerDesktopIcon,
  PuzzlePieceIcon,
  TrophyIcon,
  WrenchScrewdriverIcon,
  SunIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  CubeIcon as CubeOutlineIcon,
  PlusIcon,
  MinusIcon,
  XMarkIcon,
  TrashIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  StarIcon,
} from "@heroicons/react/24/outline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleArrowRight } from "@fortawesome/free-solid-svg-icons";

/* --------------------------------------------------------------------------
 * Catalogue : helpers calcules une seule fois au chargement du module.
 * ----------------------------------------------------------------------- */

// Icone de repli par categorie, utilisee quand le produit n'a pas encore de
// visuel dedie dans /icones.
const CATEGORY_ICONS = {
  Cartons: ArchiveBoxIcon,
  "Séjour": HomeOutlineIcon,
  Cuisine: FireIcon,
  Chambres: MoonIcon,
  "Salle de bain": SparklesIcon,
  "Bureau & Multimédia": ComputerDesktopIcon,
  "Enfants & Bébé": PuzzlePieceIcon,
  "Sport & Loisirs": TrophyIcon,
  "Garage & Cave": WrenchScrewdriverIcon,
  "Jardin & Extérieur": SunIcon,
  "Objets spéciaux": ShieldCheckIcon,
  "Divers & Déco": Squares2X2Icon,
};

const getCategoryIcon = (categoryName) =>
  CATEGORY_ICONS[categoryName] || CubeOutlineIcon;

// "Vélo" doit ressortir quand on tape "velo", "Étagère" quand on tape "etagere".
const normalize = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const ALL_PRODUCTS = Product.categories.flatMap((category) =>
  category.products.map((product) => ({
    ...product,
    _category: category.name,
    _search: normalize(
      [product.name, category.name, ...(product.synonymes || [])].join(" ")
    ),
  }))
);

const PRODUCT_BY_NAME = ALL_PRODUCTS.reduce((acc, product) => {
  acc[product.name] = product;
  return acc;
}, {});

const TOTAL_PRODUCTS = ALL_PRODUCTS.length;

// Raccourci vers les objets presents dans presque tous les logements : evite de
// faire defiler 254 references pour trouver un lit ou un carton.
const POPULAR_CATEGORY = "Populaires";
const POPULAR_PRODUCTS = ALL_PRODUCTS.filter(
  (product) => product.popularite >= 5
);

const findProduct = (name) => PRODUCT_BY_NAME[name];

// 500 x 500 x 1200 mm -> "50 × 50 × 120 cm"
const formatDimensions = (dimensions) =>
  Array.isArray(dimensions) && dimensions.length === 3
    ? `${dimensions.map((mm) => Math.round(mm / 10)).join(" × ")} cm`
    : null;

// 0.029 -> "0.029", 0.300 -> "0.3", 2 -> "2"
/* --------------------------------------------------------------------------
 * Theme : les couleurs viennent des reglages du widget (btnColor,
 * backgroundColor, labelColor). On en derive des teintes (fond doux, anneau de
 * focus) et une couleur de texte lisible, injectees en variables CSS.
 * ----------------------------------------------------------------------- */

const parseColor = (value) => {
  if (!value) return null;
  const color = String(value).trim();
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(color);
  const hex = short
    ? `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`
    : color;
  const full = /^#([0-9a-f]{6})$/i.exec(hex);
  if (full) {
    const int = parseInt(full[1], 16);
    return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
  }
  const rgb = /^rgba?\(([^)]+)\)$/i.exec(color);
  if (rgb) {
    const parts = rgb[1].split(",").map((part) => parseFloat(part));
    if (parts.length >= 3 && parts.slice(0, 3).every((n) => !isNaN(n))) {
      return { r: parts[0], g: parts[1], b: parts[2] };
    }
  }
  return null;
};

const withAlpha = (value, alpha, fallback) => {
  const rgb = parseColor(value);
  return rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})` : fallback;
};

// Noir ou blanc selon la luminance du fond, pour rester lisible quel que soit
// le coloris choisi par le demenageur.
const readableOn = (value, fallback = "#ffffff") => {
  const rgb = parseColor(value);
  if (!rgb) return fallback;
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.62 ? "#0f172a" : "#ffffff";
};

// Reperes de volume : aide le visiteur a se projeter (et le devis a etre credible)
const TRUCK_SIZES = [12, 20, 30, 50];

const getTruckHint = (volume) => {
  if (volume <= 0) return null;
  const size = TRUCK_SIZES.find((capacity) => volume <= capacity);
  if (!size) {
    const trucks = Math.ceil(volume / 50);
    return { label: `${trucks} camions de 50 m³`, capacity: trucks * 50 };
  }
  return { label: `un camion de ${size} m³`, capacity: size };
};

const formatVolume = (volume) =>
  Number(volume)
    .toFixed(3)
    .replace(/0+$/, "")
    .replace(/\.$/, "");

const DemenageurWidget = ({ widgetId }) => {
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("Tous");
  const [quantities, setQuantities] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [widgets, setWidgets] = useState([]);
  const [inventaire, setInventaire] = useState(false);
  const [clientInfo, setClientInfo] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [formStep, setFormStep] = useState(0);
  // Objets absents du catalogue, saisis a la main par le visiteur.
  const [customItems, setCustomItems] = useState([]);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customDraft, setCustomDraft] = useState({
    name: "",
    length: "",
    width: "",
    height: "",
  });
  const [customError, setCustomError] = useState("");

  useEffect(() => {
    setIsLoading(false);
  }, []);

  const handleCategoryClick = (categoryName) => {
    setSelectedCategory(categoryName);
    setSearchTerm("");
  };

  // Une recherche porte sur tout le catalogue, pas seulement sur la categorie
  // affichee : sinon taper "velo" depuis "Chambres" ne renvoie rien.
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (value && selectedCategory !== "Tous") setSelectedCategory("Tous");
  };

  const handleQuantityChange = (productName, delta) => {
    setQuantities((prev) => ({
      ...prev,
      [productName]: Math.max((prev[productName] || 0) + delta, 0),
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClientInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const normalizedSearch = normalize(searchTerm).trim();

  const filteredProducts = ALL_PRODUCTS.filter((product) => {
    const inCategory =
      selectedCategory === "Tous" ||
      (selectedCategory === POPULAR_CATEGORY
        ? product.popularite >= 5
        : product._category === selectedCategory);
    if (!inCategory) return false;
    if (!normalizedSearch) return true;
    // Recherche insensible aux accents, sur le nom, la categorie et les synonymes.
    return normalizedSearch
      .split(/\s+/)
      .every((token) => product._search.includes(token));
  }).sort((a, b) => {
    if (!normalizedSearch) return 0;
    // Les correspondances sur le debut du nom remontent en premier.
    const score = (product) =>
      normalize(product.name).startsWith(normalizedSearch)
        ? 2
        : normalize(product.name).includes(normalizedSearch)
        ? 1
        : 0;
    return score(b) - score(a) || (b.popularite || 0) - (a.popularite || 0);
  });

  const cartonIconClass = (volume) => {
    if (volume <= 0.04)
      return "w-[26px] h-[26px] sm:w-[32px] sm:h-[32px] transition-all";
    if (volume <= 0.07)
      return "w-[36px] h-[36px] sm:w-[44px] sm:h-[44px] transition-all";
    if (volume <= 0.12)
      return "w-[46px] h-[46px] sm:w-[54px] sm:h-[54px] transition-all";
    return "w-[56px] h-[56px] sm:w-[64px] sm:h-[64px] transition-all";
  };

  const catalogVolume = ALL_PRODUCTS.reduce(
    (total, product) =>
      total + (quantities[product.name] || 0) * product.volume_m3,
    0
  );

  const customVolume = customItems.reduce(
    (total, item) => total + item.quantity * item.volume_m3,
    0
  );

  const totalVolume = catalogVolume + customVolume;

  useEffect(() => {
    const fetchWidget = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/widgets/demenageur/getOneWidget/${widgetId}`
        );
        if (response.status === 200) {
          setWidgets(response.data.widget);
        } else {
          console.error("Erreur lors de la récupération des widgets");
        }
      } catch (error) {
        setIsError(true);
      }
    };

    fetchWidget();
  }, [widgetId]);

  const inventoryProducts = Object.keys(quantities).filter(
    (productName) => quantities[productName] > 0
  );

  const handleFormSubmit = (e) => {
    e.preventDefault();

    // Construire l'inventaire et le volume total
    const catalogLines = inventoryProducts.map((productName) => {
      const product = findProduct(productName);
      const quantity = quantities[productName];
      const volume = (quantity * (product?.volume_m3 || 0)).toFixed(2);
      const flags = [
        product?.lourd ? "objet lourd" : null,
        product?.fragile ? "fragile" : null,
        product?.demontable ? "à démonter" : null,
      ].filter(Boolean);

      return `${productName} : ${quantity} x ${(product?.volume_m3 || 0).toFixed(
        3
      )} m³ = ${volume} m³${flags.length ? ` (${flags.join(", ")})` : ""}`;
    });

    const customLines = customItems.map(
      (item) =>
        `${item.name} (objet non listé, ${formatDimensions(
          item.dimensions_mm
        )}) : ${item.quantity} x ${item.volume_m3.toFixed(3)} m³ = ${(
          item.quantity * item.volume_m3
        ).toFixed(2)} m³`
    );

    const inventoryDetails = [...catalogLines, ...customLines].join("\n");

    // Points de vigilance pour le devis (manutention, emballage, demontage).
    const heavyItems = inventoryProducts.filter(
      (name) => findProduct(name)?.lourd
    );
    const fragileItems = inventoryProducts.filter(
      (name) => findProduct(name)?.fragile
    );
    const toDismantle = inventoryProducts.filter(
      (name) => findProduct(name)?.demontable
    );

    const pointsDeVigilance = [
      heavyItems.length ? `Objets lourds : ${heavyItems.join(", ")}` : null,
      fragileItems.length
        ? `Objets fragiles : ${fragileItems.join(", ")}`
        : null,
      toDismantle.length
        ? `Démontage / remontage à prévoir : ${toDismantle.join(", ")}`
        : null,
    ]
      .filter(Boolean)
      .join("\n");

    const totalVolumeStr = totalVolume.toFixed(2);

    // Construire le contenu de l'email
    const emailBody = `
Bonjour,

Je souhaiterais obtenir un devis pour mon futur déménagement. Vous trouverez ci-dessous les détails nécessaires à l'établissement de ce devis.

INFORMATIONS PERSONNELLES :

Nom : ${clientInfo.name}
Téléphone : ${clientInfo.phoneNumber}
Email : ${clientInfo.email}

ADRESSE DE DÉPART :

Adresse : ${clientInfo.currentAddress}
Code postal : ${clientInfo.currentPostalCode}
Ville : ${clientInfo.currentCity}
Surface : ${clientInfo.surface ? clientInfo.surface + " m²" : "Non spécifiée"}

ADRESSE D'ARRIVÉE :

Code postal : ${clientInfo.newPostalCode}
Ville : ${clientInfo.newCity}
Statut : ${
      clientInfo.ownershipStatus ? clientInfo.ownershipStatus : "Non spécifié"
    }

DATE DE DÉMÉNAGEMENT :

${clientInfo.moveDate}

DÉTAILS SUPPLÉMENTAIRES CONCERNANT LE DÉMÉNAGEMENT :

${
  clientInfo.additionalDetails
    ? clientInfo.additionalDetails
    : "Aucune précision fournie"
}

INVENTAIRE ET VOLUME TOTAL ESTIMÉ :

${inventoryDetails}

VOLUME TOTAL : ${totalVolumeStr} m³
${
  pointsDeVigilance
    ? `
POINTS DE VIGILANCE :

${pointsDeVigilance}
`
    : ""
}

Je reste à votre disposition pour toute information complémentaire.

Cordialement,

${clientInfo.name}
`;

    const mailtoLink = `mailto:${
      widgets.contactMail
    }?subject=Demande de devis déménagement&body=${encodeURIComponent(
      emailBody
    )}`;

    window.location.href = mailtoLink;
  };

  const handleAddCustomItem = () => {
    const name = customDraft.name.trim();
    const length = parseFloat(customDraft.length);
    const width = parseFloat(customDraft.width);
    const height = parseFloat(customDraft.height);

    if (!name) {
      setCustomError("Merci d'indiquer le nom de l'objet.");
      return;
    }
    if (
      !(length > 0) ||
      !(width > 0) ||
      !(height > 0) ||
      length > 1000 ||
      width > 1000 ||
      height > 1000
    ) {
      setCustomError("Indiquez des dimensions en centimètres (entre 1 et 1000).");
      return;
    }

    const volume = Math.max(
      Math.round(((length * width * height) / 1000000) * 1000) / 1000,
      0.01
    );

    setCustomItems((prev) => [
      ...prev,
      {
        id: `custom-${prev.length}-${name}`,
        name,
        dimensions_mm: [length * 10, width * 10, height * 10],
        volume_m3: volume,
        quantity: 1,
      },
    ]);
    setCustomDraft({ name: "", length: "", width: "", height: "" });
    setCustomError("");
    setShowCustomForm(false);
  };

  const handleCustomQuantityChange = (id, delta) => {
    setCustomItems((prev) =>
      prev
        .map((item) =>
          item.id === id
            ? { ...item, quantity: Math.max(item.quantity + delta, 0) }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const handleDeleteCustomItem = (id) => {
    setCustomItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleClearInventory = () => {
    setQuantities({});
    setCustomItems([]);
  };

  const openCustomForm = () => {
    setInventaire(true);
    setShowCustomForm(true);
  };

  const handleDeleteProduct = (productName) => {
    setQuantities((prevQuantities) => {
      const updatedQuantities = { ...prevQuantities };
      delete updatedQuantities[productName];
      return updatedQuantities;
    });
  };

  // Palette derivee des reglages du widget, exposee en variables CSS.
  const accentColor = widgets?.btnColor || "#2563eb";
  const surfaceColor = widgets?.backgroundColor || "#f8fafc";
  const onAccentColor = widgets?.labelColor || readableOn(accentColor);
  const onSurfaceColor = readableOn(surfaceColor, "#0f172a");

  const themeStyle = {
    "--dw-accent": accentColor,
    "--dw-on-accent": onAccentColor,
    "--dw-surface": surfaceColor,
    "--dw-on-surface": onSurfaceColor,
    "--dw-accent-soft": withAlpha(accentColor, 0.1, "rgba(37, 99, 235, 0.1)"),
    "--dw-accent-ring": withAlpha(accentColor, 0.32, "rgba(37, 99, 235, 0.32)"),
    // conserve la variable historique utilisee par .widget-input:focus
    "--widget-color": accentColor,
  };

  const itemCount =
    inventoryProducts.reduce((total, name) => total + quantities[name], 0) +
    customItems.reduce((total, item) => total + item.quantity, 0);

  // Inventaire regroupe par piece : plus lisible qu'une longue liste a plat.
  const inventoryByCategory = inventoryProducts.reduce((groups, name) => {
    const category = findProduct(name)?._category || "Autres";
    if (!groups[category]) groups[category] = [];
    groups[category].push(name);
    return groups;
  }, {});

  const truckHint = getTruckHint(totalVolume);
  const truckFill = truckHint
    ? Math.min((totalVolume / truckHint.capacity) * 100, 100)
    : 0;

  const totalPrice =
    (widgets?.pricePerM3 || 0) * totalVolume + (widgets?.voyageVidePrice || 0);

  const inputClass =
    "widget-input p-2.5 border border-gray-300 w-full rounded-lg transition-shadow";

  return (
    <>
      {isError ? (
        <div className="flex items-center justify-center h-screen w-screen antialiased text-center bg-stone-100 text-gray-800 font-openSans">
          <article className="mx-auto max-w-screen-md p-6 sm:p-12 items-center">
            <h3 className="text-2xl sm:text-3xl font-light mb-8 sm:mb-12">
              Le simulateur est désactivé !
            </h3>
            <p className="text-lg sm:text-xl text-gray-800 mb-8">
              Merci de contacter notre support{" "}
              <a
                className="text-blue-800 hover:text-blue-700 mb-8"
                href="https://local.fr"
              >
                Local.fr
              </a>{" "}
              pour plus d'informations.
            </p>
            <p className="font-bold">Simulateur de volume avec Local.fr</p>
          </article>
        </div>
      ) : (
        <div
          className="dw-root font-sans flex flex-col w-full max-h-[90vh] bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-[0_18px_40px_-24px_rgba(15,23,42,0.45)]"
          style={themeStyle}
        >
          {/* En-tete : recherche + bascule catalogue / inventaire */}
          {!showForm && (
            <div
              className="head dw-surface-bg w-full px-3 py-3 sm:px-4 sm:py-3.5 border-b border-black/5"
              style={{ backgroundColor: surfaceColor, color: onSurfaceColor }}
            >
              <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 sm:items-center">
                <div className="search relative flex-1 min-w-0">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Rechercher un meuble, un carton…"
                    aria-label="Rechercher un objet dans le catalogue"
                    className="widget-input w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-10 text-sm sm:text-base text-gray-900 placeholder:text-gray-400 shadow-sm"
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onFocus={() => setInventaire(false)}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      aria-label="Effacer la recherche"
                      className="dw-focus absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      onClick={() => handleSearchChange("")}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div
                  className="inventory flex shrink-0 rounded-xl bg-black/5 p-1"
                  role="tablist"
                  aria-label="Affichage"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={!inventaire}
                    className="dw-segment dw-focus dw-press rounded-lg px-3 py-1.5 text-sm font-medium"
                    onClick={() => setInventaire(false)}
                  >
                    Catalogue
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={inventaire}
                    className="dw-segment dw-focus dw-press flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium"
                    onClick={() => setInventaire(true)}
                  >
                    Inventaire
                    <span
                      className={`min-w-[20px] rounded-full px-1.5 text-xs font-bold ${
                        inventoryProducts.length + customItems.length > 0
                          ? "dw-accent-bg"
                          : "bg-black/10"
                      }`}
                    >
                      {inventoryProducts.length + customItems.length}
                    </span>
                  </button>
                </div>
              </div>

              {!inventaire && searchTerm && (
                <p className="mt-2 text-xs opacity-70">
                  {filteredProducts.length}{" "}
                  {filteredProducts.length > 1 ? "résultats" : "résultat"} pour «{" "}
                  {searchTerm} »
                </p>
              )}
            </div>
          )}

          {showForm ? (
            <div className="form-container w-full max-h-[88vh] overflow-y-auto bg-gray-50 styled-scrollbar">
              {/* Stepper progress bar */}
              <div className="w-full px-4 sm:px-8 pt-4 pb-2">
                <div className="flex items-center justify-between">
                  {[
                    { icon: HomeIcon, label: "Départ" },
                    { icon: HomeModernIcon, label: "Arrivée" },
                    { icon: UserIcon, label: "Infos" },
                    { icon: ClipboardDocumentListIcon, label: "Récap" },
                  ].map((step, index) => (
                    <div key={index} className="flex items-center flex-1 last:flex-none">
                      <div className="flex flex-col items-center">
                        <button
                          type="button"
                          onClick={() => index < formStep && setFormStep(index)}
                          className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all ${
                            index < formStep
                              ? "cursor-pointer"
                              : index === formStep
                              ? ""
                              : "cursor-default"
                          }`}
                          style={
                            index <= formStep
                              ? { backgroundColor: widgets?.btnColor, color: "white" }
                              : { backgroundColor: "#e5e7eb", color: "#9ca3af" }
                          }
                        >
                          {index < formStep ? (
                            <CheckIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                          ) : (
                            <step.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                          )}
                        </button>
                        <span
                          className={`text-[10px] sm:text-xs mt-1 font-medium ${
                            index <= formStep ? "text-gray-700" : "text-gray-400"
                          }`}
                        >
                          {step.label}
                        </span>
                      </div>
                      {index < 3 && (
                        <div
                          className="flex-1 h-0.5 mx-1 sm:mx-2 rounded-full mb-4"
                          style={
                            index < formStep
                              ? { backgroundColor: widgets?.btnColor }
                              : { backgroundColor: "#e5e7eb" }
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <form onSubmit={handleFormSubmit} className="flex flex-col gap-4 p-3 sm:p-4 pt-2">
                {/* Step 0: Logement actuel */}
                {formStep === 0 && (
                  <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <HomeIcon className="w-5 h-5 text-gray-500" />
                      <h3 className="text-base sm:text-lg font-bold">Votre logement actuel</h3>
                    </div>
                    <div className="flex flex-col gap-3 sm:gap-4">
                      <div>
                        <label className="text-sm text-gray-600 mb-1 block">Adresse</label>
                        <input
                          type="text"
                          name="currentAddress"
                          placeholder="Numéro et nom de rue"
                          className={inputClass}
                          value={clientInfo.currentAddress || ""}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">Code postal *</label>
                          <input
                            type="text"
                            name="currentPostalCode"
                            placeholder="Ex: 75001"
                            className={inputClass}
                            value={clientInfo.currentPostalCode || ""}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">Ville *</label>
                          <input
                            type="text"
                            name="currentCity"
                            placeholder="Ex: Paris"
                            className={inputClass}
                            value={clientInfo.currentCity || ""}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600 mb-1 block">
                          Surface du logement
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            name="surface"
                            placeholder="Ex: 65"
                            className={`${inputClass} pr-12`}
                            value={clientInfo.surface || ""}
                            onChange={handleInputChange}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                            m²
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 1: Nouveau logement */}
                {formStep === 1 && (
                  <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <HomeModernIcon className="w-5 h-5 text-gray-500" />
                      <h3 className="text-base sm:text-lg font-bold">Votre nouveau logement</h3>
                    </div>
                    <div className="flex flex-col gap-3 sm:gap-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">Code postal *</label>
                          <input
                            type="text"
                            name="newPostalCode"
                            placeholder="Ex: 69001"
                            className={inputClass}
                            value={clientInfo.newPostalCode || ""}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">Ville *</label>
                          <input
                            type="text"
                            name="newCity"
                            placeholder="Ex: Lyon"
                            className={inputClass}
                            value={clientInfo.newCity || ""}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600 mb-1 block">Vous serez</label>
                        <div className="grid grid-cols-2 gap-3">
                          {["proprietaire", "locataire"].map((status) => (
                            <button
                              key={status}
                              type="button"
                              onClick={() =>
                                handleInputChange({
                                  target: { name: "ownershipStatus", value: status },
                                })
                              }
                              className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                                clientInfo.ownershipStatus === status
                                  ? "border-transparent text-white"
                                  : "border-gray-200 text-gray-600 hover:border-gray-300"
                              }`}
                              style={
                                clientInfo.ownershipStatus === status
                                  ? { backgroundColor: widgets?.btnColor }
                                  : {}
                              }
                            >
                              {status === "proprietaire" ? "Propriétaire" : "Locataire"}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Infos personnelles + date */}
                {formStep === 2 && (
                  <div className="flex flex-col gap-4">
                    <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm">
                      <div className="flex items-center gap-2 mb-4">
                        <UserIcon className="w-5 h-5 text-gray-500" />
                        <h3 className="text-base sm:text-lg font-bold">Vos coordonnées</h3>
                      </div>
                      <div className="flex flex-col gap-3 sm:gap-4">
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">Nom complet *</label>
                          <input
                            type="text"
                            name="name"
                            placeholder="Prénom Nom"
                            className={inputClass}
                            value={clientInfo.name || ""}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                          <div>
                            <label className="text-sm text-gray-600 mb-1 block">Téléphone *</label>
                            <input
                              type="tel"
                              name="phoneNumber"
                              placeholder="06 12 34 56 78"
                              className={inputClass}
                              value={clientInfo.phoneNumber || ""}
                              onChange={handleInputChange}
                              required
                            />
                          </div>
                          <div>
                            <label className="text-sm text-gray-600 mb-1 block">E-mail *</label>
                            <input
                              type="email"
                              name="email"
                              placeholder="votre@email.com"
                              className={inputClass}
                              value={clientInfo.email || ""}
                              onChange={handleInputChange}
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm">
                      <div className="flex items-center gap-2 mb-4">
                        <CalendarDaysIcon className="w-5 h-5 text-gray-500" />
                        <h3 className="text-base sm:text-lg font-bold">Date & précisions</h3>
                      </div>
                      <div className="flex flex-col gap-3 sm:gap-4">
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">
                            Date souhaitée de déménagement *
                          </label>
                          <input
                            type="date"
                            name="moveDate"
                            className={inputClass}
                            value={clientInfo.moveDate || ""}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div>
                          <label className="text-sm text-gray-600 mb-1 block">
                            Précisions complémentaires
                          </label>
                          <textarea
                            name="additionalDetails"
                            placeholder="Étage, ascenseur, objets fragiles, accès difficile..."
                            className={`${inputClass} min-h-[100px] resize-none`}
                            value={clientInfo.additionalDetails || ""}
                            onChange={handleInputChange}
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Récapitulatif */}
                {formStep === 3 && (
                  <div className="flex flex-col gap-3">
                    <div className="bg-white p-4 sm:p-6 rounded-xl border border-gray-100 shadow-sm">
                      <div className="flex items-center gap-2 mb-4">
                        <ClipboardDocumentListIcon className="w-5 h-5 text-gray-500" />
                        <h3 className="text-base sm:text-lg font-bold">Récapitulatif</h3>
                      </div>

                      {/* Addresses recap */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center gap-1.5 mb-2">
                            <HomeIcon className="w-4 h-4 text-gray-400" />
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Départ</p>
                          </div>
                          <p className="text-sm font-medium">
                            {clientInfo.currentAddress || "—"}
                          </p>
                          <p className="text-sm text-gray-600">
                            {clientInfo.currentPostalCode} {clientInfo.currentCity}
                          </p>
                          {clientInfo.surface && (
                            <p className="text-xs text-gray-400 mt-1">{clientInfo.surface} m²</p>
                          )}
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center gap-1.5 mb-2">
                            <HomeModernIcon className="w-4 h-4 text-gray-400" />
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Arrivée</p>
                          </div>
                          <p className="text-sm font-medium">
                            {clientInfo.newPostalCode} {clientInfo.newCity}
                          </p>
                          {clientInfo.ownershipStatus && (
                            <p className="text-xs text-gray-400 mt-1 capitalize">
                              {clientInfo.ownershipStatus}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Contact recap */}
                      <div className="bg-gray-50 p-3 rounded-lg mb-4">
                        <div className="flex items-center gap-1.5 mb-2">
                          <UserIcon className="w-4 h-4 text-gray-400" />
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Contact</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1">
                          <p className="text-sm">{clientInfo.name}</p>
                          <p className="text-sm text-gray-600">{clientInfo.phoneNumber}</p>
                          <p className="text-sm text-gray-600">{clientInfo.email}</p>
                        </div>
                      </div>

                      {/* Date & details */}
                      <div className="bg-gray-50 p-3 rounded-lg mb-4">
                        <div className="flex items-center gap-1.5 mb-2">
                          <CalendarDaysIcon className="w-4 h-4 text-gray-400" />
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Déménagement</p>
                        </div>
                        <p className="text-sm font-medium">{clientInfo.moveDate || "—"}</p>
                        {clientInfo.additionalDetails && (
                          <p className="text-xs text-gray-500 mt-1">
                            {clientInfo.additionalDetails}
                          </p>
                        )}
                      </div>

                      {/* Inventory recap */}
                      {inventoryProducts.length + customItems.length > 0 && (
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center gap-1.5 mb-2">
                            <CubeIcon className="w-4 h-4 text-gray-400" />
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                              Inventaire (
                              {inventoryProducts.length + customItems.length}{" "}
                              articles)
                            </p>
                          </div>
                          <div className="flex flex-col gap-1.5 max-h-[150px] overflow-y-auto styled-scrollbar">
                            {inventoryProducts.map((productName) => {
                              const product = findProduct(productName);
                              return (
                                <div
                                  key={productName}
                                  className="flex items-center justify-between text-sm"
                                >
                                  <span className="text-gray-700">{productName}</span>
                                  <span className="text-gray-400 text-xs">
                                    x{quantities[productName]} &middot;{" "}
                                    {(
                                      quantities[productName] *
                                      (product?.volume_m3 || 0)
                                    ).toFixed(2)}{" "}
                                    m³
                                  </span>
                                </div>
                              );
                            })}
                            {customItems.map((item) => (
                              <div
                                key={item.id}
                                className="flex items-center justify-between text-sm"
                              >
                                <span className="text-gray-700">
                                  {item.name}{" "}
                                  <span className="text-gray-400 text-xs">
                                    (objet non listé)
                                  </span>
                                </span>
                                <span className="text-gray-400 text-xs">
                                  x{item.quantity} &middot;{" "}
                                  {(item.quantity * item.volume_m3).toFixed(2)} m³
                                </span>
                              </div>
                            ))}
                          </div>
                          <div
                            className="mt-3 pt-2 border-t border-gray-200 flex justify-between items-center font-bold text-sm"
                          >
                            <span>Volume total</span>
                            <span
                              className="px-2 py-1 rounded-md text-white text-xs"
                              style={{ backgroundColor: widgets?.btnColor }}
                            >
                              {totalVolume.toFixed(2)} m³
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Navigation buttons */}
                <div className="flex items-center gap-2 sm:gap-3 pt-2 pb-1">
                  <button
                    type="button"
                    className={`flex items-center justify-center gap-1.5 sm:gap-2 p-3 rounded-xl text-sm font-medium transition-all ${
                      formStep === 0
                        ? "border-2 border-gray-200 text-gray-500 hover:bg-gray-100 flex-1"
                        : "border-2 border-gray-200 text-gray-600 hover:bg-gray-100"
                    }`}
                    onClick={() => {
                      if (formStep === 0) {
                        setShowForm(false);
                        setFormStep(0);
                      } else {
                        setFormStep(formStep - 1);
                      }
                    }}
                  >
                    <ArrowLeftIcon className="w-4 h-4 shrink-0" />
                    <span className="hidden sm:inline">
                      {formStep === 0 ? "Retour aux produits" : "Précédent"}
                    </span>
                    <span className="sm:hidden">
                      {formStep === 0 ? "Retour" : "Retour"}
                    </span>
                  </button>

                  {formStep < 3 ? (
                    <button
                      type="button"
                      className="flex-1 flex items-center justify-center gap-1.5 sm:gap-2 p-3 rounded-xl text-sm font-medium text-white transition-opacity hover:opacity-90"
                      style={{ backgroundColor: widgets?.btnColor }}
                      onClick={() => setFormStep(formStep + 1)}
                    >
                      Suivant
                      <ArrowRightIcon className="w-4 h-4 shrink-0" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="flex-1 flex items-center justify-center gap-1.5 sm:gap-2 p-3 rounded-xl text-sm font-medium text-white transition-opacity hover:opacity-90"
                      style={{ backgroundColor: widgets?.btnColor }}
                      onClick={handleFormSubmit}
                    >
                      <span className="hidden sm:inline">Envoyer ma demande</span>
                      <span className="sm:hidden">Envoyer</span>
                      <FontAwesomeIcon
                        icon={faCircleArrowRight}
                        color={widgets?.labelColor}
                      />
                    </button>
                  )}
                </div>
              </form>
            </div>
          ) : inventaire ? (
            <div className="inventory-list flex h-[450px] w-full flex-col gap-3 overflow-y-auto styled-scrollbar p-3 sm:p-5">
              {/* Objet absent du catalogue : saisie libre nom + dimensions */}
              {showCustomForm ? (
                <div className="w-full rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-3 sm:p-4">
                  <p className="mb-2 text-sm font-semibold text-gray-900">
                    Ajouter un objet non listé
                  </p>
                  <input
                    type="text"
                    placeholder="Nom de l'objet (ex : vitrine d'atelier)"
                    aria-label="Nom de l'objet"
                    className="widget-input mb-2 w-full rounded-xl border border-gray-200 bg-white p-2.5 text-sm"
                    value={customDraft.name}
                    onChange={(e) =>
                      setCustomDraft({ ...customDraft, name: e.target.value })
                    }
                  />
                  <div className="mb-2 grid grid-cols-3 gap-2">
                    {[
                      { key: "length", label: "Longueur (cm)" },
                      { key: "width", label: "Largeur (cm)" },
                      { key: "height", label: "Hauteur (cm)" },
                    ].map((field) => (
                      <input
                        key={field.key}
                        type="number"
                        min="1"
                        placeholder={field.label}
                        aria-label={field.label}
                        className="widget-input w-full rounded-xl border border-gray-200 bg-white p-2.5 text-sm"
                        value={customDraft[field.key]}
                        onChange={(e) =>
                          setCustomDraft({
                            ...customDraft,
                            [field.key]: e.target.value,
                          })
                        }
                      />
                    ))}
                  </div>
                  {customError && (
                    <p className="mb-2 text-xs text-red-500" role="alert">
                      {customError}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="dw-accent-bg dw-press dw-focus rounded-xl px-4 py-2 text-sm font-medium"
                      onClick={handleAddCustomItem}
                    >
                      Ajouter
                    </button>
                    <button
                      type="button"
                      className="dw-press dw-focus rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600"
                      onClick={() => {
                        setShowCustomForm(false);
                        setCustomError("");
                      }}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    className="dw-press dw-focus flex flex-1 items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 p-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
                    onClick={() => setShowCustomForm(true)}
                  >
                    <PlusIcon className="h-4 w-4" />
                    Ajouter un objet non listé
                  </button>
                  {inventoryProducts.length + customItems.length > 0 && (
                    <button
                      type="button"
                      className="dw-press dw-focus rounded-xl px-3 py-2.5 text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-red-600"
                      onClick={handleClearInventory}
                    >
                      Tout vider
                    </button>
                  )}
                </div>
              )}

              {inventoryProducts.length + customItems.length > 0 ? (
                <>
                  {Object.entries(inventoryByCategory).map(
                    ([category, names]) => {
                      const CategoryIcon = getCategoryIcon(category);
                      const categoryVolume = names.reduce(
                        (total, name) =>
                          total +
                          quantities[name] * (findProduct(name)?.volume_m3 || 0),
                        0
                      );
                      return (
                        <section key={category} className="w-full">
                          <div className="mb-1.5 flex items-center gap-2 px-1">
                            <CategoryIcon className="h-4 w-4 text-gray-400" />
                            <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500">
                              {category}
                            </h3>
                            <span className="ml-auto text-xs font-medium text-gray-400">
                              {categoryVolume.toFixed(2)} m³
                            </span>
                          </div>

                          <ul className="flex flex-col gap-2">
                            {names.map((productName) => {
                              const product = findProduct(productName);
                              const dimensions = formatDimensions(
                                product?.dimensions_mm
                              );
                              return (
                                <li
                                  key={productName}
                                  className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200/80 bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                                >
                                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-50">
                                    {product?.image ? (
                                      <img
                                        src={`${process.env.REACT_APP_API_URL}/${product.image}`}
                                        alt=""
                                        aria-hidden="true"
                                        className="h-6 w-6 object-contain"
                                      />
                                    ) : (
                                      <CategoryIcon className="h-5 w-5 text-gray-400" />
                                    )}
                                  </span>

                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-medium text-gray-900">
                                      {productName}
                                    </p>
                                    <p className="text-[11px] text-gray-400">
                                      {dimensions ? `${dimensions} · ` : ""}
                                      {formatVolume(product?.volume_m3 || 0)} m³
                                      / unité
                                      {product?.demontable ? " · à démonter" : ""}
                                    </p>
                                  </div>

                                  <div className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      aria-label={`Retirer un ${productName}`}
                                      className="dw-press dw-focus flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                                      onClick={() =>
                                        handleQuantityChange(productName, -1)
                                      }
                                    >
                                      <MinusIcon className="h-4 w-4" />
                                    </button>
                                    <span className="min-w-[28px] text-center text-sm font-bold">
                                      {quantities[productName]}
                                    </span>
                                    <button
                                      type="button"
                                      aria-label={`Ajouter un ${productName}`}
                                      className="dw-accent-bg dw-press dw-focus flex h-8 w-8 items-center justify-center rounded-full"
                                      onClick={() =>
                                        handleQuantityChange(productName, 1)
                                      }
                                    >
                                      <PlusIcon className="h-4 w-4" />
                                    </button>
                                  </div>

                                  <span className="dw-soft-bg dw-accent-text w-[74px] shrink-0 rounded-lg px-2 py-1 text-center text-xs font-bold">
                                    {(
                                      quantities[productName] *
                                      (product?.volume_m3 || 0)
                                    ).toFixed(2)}{" "}
                                    m³
                                  </span>

                                  <button
                                    type="button"
                                    aria-label={`Supprimer ${productName}`}
                                    className="dw-press dw-focus rounded-full p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
                                    onClick={() =>
                                      handleDeleteProduct(productName)
                                    }
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </section>
                      );
                    }
                  )}

                  {customItems.length > 0 && (
                    <section className="w-full">
                      <div className="mb-1.5 flex items-center gap-2 px-1">
                        <CubeOutlineIcon className="h-4 w-4 text-gray-400" />
                        <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500">
                          Objets non listés
                        </h3>
                        <span className="ml-auto text-xs font-medium text-gray-400">
                          {customVolume.toFixed(2)} m³
                        </span>
                      </div>

                      <ul className="flex flex-col gap-2">
                        {customItems.map((item) => (
                          <li
                            key={item.id}
                            className="flex flex-wrap items-center gap-3 rounded-2xl border border-dashed border-gray-300 bg-white p-3"
                          >
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-50">
                              <CubeOutlineIcon className="h-5 w-5 text-gray-400" />
                            </span>

                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-gray-900">
                                {item.name}
                              </p>
                              <p className="text-[11px] text-gray-400">
                                {formatDimensions(item.dimensions_mm)} ·{" "}
                                {formatVolume(item.volume_m3)} m³ / unité
                              </p>
                            </div>

                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                aria-label={`Retirer un ${item.name}`}
                                className="dw-press dw-focus flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                                onClick={() =>
                                  handleCustomQuantityChange(item.id, -1)
                                }
                              >
                                <MinusIcon className="h-4 w-4" />
                              </button>
                              <span className="min-w-[28px] text-center text-sm font-bold">
                                {item.quantity}
                              </span>
                              <button
                                type="button"
                                aria-label={`Ajouter un ${item.name}`}
                                className="dw-accent-bg dw-press dw-focus flex h-8 w-8 items-center justify-center rounded-full"
                                onClick={() =>
                                  handleCustomQuantityChange(item.id, 1)
                                }
                              >
                                <PlusIcon className="h-4 w-4" />
                              </button>
                            </div>

                            <span className="dw-soft-bg dw-accent-text w-[74px] shrink-0 rounded-lg px-2 py-1 text-center text-xs font-bold">
                              {(item.quantity * item.volume_m3).toFixed(2)} m³
                            </span>

                            <button
                              type="button"
                              aria-label={`Supprimer ${item.name}`}
                              className="dw-press dw-focus rounded-full p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
                              onClick={() => handleDeleteCustomItem(item.id)}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                </>
              ) : (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center text-gray-400">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-50">
                    <CubeIcon className="h-8 w-8 opacity-30" />
                  </span>
                  <div>
                    <p className="text-base font-medium text-gray-600">
                      Votre inventaire est vide
                    </p>
                    <p className="text-sm">
                      Ajoutez vos meubles et cartons depuis le catalogue.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="dw-accent-bg dw-press dw-focus rounded-xl px-4 py-2 text-sm font-medium"
                    onClick={() => setInventaire(false)}
                  >
                    Parcourir le catalogue
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Categories : boutons accessibles au clavier, defilement horizontal */}
              <div
                className="filter flex w-full gap-2 overflow-x-auto border-b border-gray-100 px-3 py-2.5 sm:px-4"
                role="group"
                aria-label="Filtrer par pièce"
              >
                {[
                  { key: "Tous", label: "Tous", count: TOTAL_PRODUCTS, Icon: Squares2X2Icon },
                  {
                    key: POPULAR_CATEGORY,
                    label: "Populaires",
                    count: POPULAR_PRODUCTS.length,
                    Icon: StarIcon,
                  },
                  ...Product.categories.map((category) => ({
                    key: category.name,
                    label: category.name,
                    count: category.products.length,
                    Icon: getCategoryIcon(category.name),
                  })),
                ].map(({ key, label, count, Icon }) => {
                  const active = selectedCategory === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={active}
                      onClick={() => handleCategoryClick(key)}
                      className={`dw-chip dw-focus dw-press flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm ${
                        active
                          ? "font-semibold"
                          : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="whitespace-nowrap">{label}</span>
                      <span
                        className={`rounded-full px-1.5 text-[11px] font-semibold ${
                          active ? "bg-white/25" : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {isLoading ? (
                <div className="grid w-full grid-cols-2 gap-3 p-3 sm:grid-cols-3 sm:gap-4 sm:p-5 md:grid-cols-4 lg:grid-cols-5">
                  {Array.from({ length: 10 }).map((_, index) => (
                    <div
                      key={index}
                      className="dw-skeleton h-[205px] rounded-2xl sm:h-[230px]"
                    />
                  ))}
                </div>
              ) : (
                <div className="products grid h-[450px] w-full grid-cols-2 gap-3 overflow-y-auto styled-scrollbar p-3 sm:grid-cols-3 sm:gap-4 sm:p-5 md:grid-cols-4 lg:grid-cols-5">
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => {
                      const quantity = quantities[product.name] || 0;
                      const CategoryIcon = getCategoryIcon(product._category);
                      const dimensions = formatDimensions(product.dimensions_mm);
                      const selected = quantity > 0;
                      return (
                        <div
                          key={product.name}
                          className={`dw-card group relative flex h-[205px] flex-col rounded-2xl border bg-white p-3 sm:h-[230px] sm:p-4 ${
                            selected
                              ? "dw-card--selected"
                              : "border-gray-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                          }`}
                        >
                          {selected && (
                            <span className="dw-accent-bg absolute right-2 top-2 flex h-6 min-w-[24px] items-center justify-center rounded-full px-1.5 text-xs font-bold">
                              {quantity}
                            </span>
                          )}

                          <div className="flex flex-1 flex-col items-center justify-center gap-1 text-center">
                            <span
                              className={`mb-0.5 flex h-12 w-12 items-center justify-center rounded-full sm:h-14 sm:w-14 ${
                                selected ? "bg-white/70" : "bg-gray-50"
                              }`}
                            >
                              {product.image ? (
                                <img
                                  src={`${process.env.REACT_APP_API_URL}/${product.image}`}
                                  alt=""
                                  aria-hidden="true"
                                  className={
                                    product._category === "Cartons"
                                      ? cartonIconClass(product.volume_m3)
                                      : "h-8 w-8 sm:h-9 sm:w-9 object-contain"
                                  }
                                />
                              ) : (
                                <CategoryIcon className="h-7 w-7 text-gray-400 sm:h-8 sm:w-8" />
                              )}
                            </span>

                            <p className="line-clamp-2 text-xs font-medium leading-tight text-gray-900 sm:text-sm">
                              {product.name}
                            </p>
                            {dimensions && (
                              <p className="text-[10px] leading-tight text-gray-400">
                                {dimensions}
                              </p>
                            )}
                            <p className="text-[11px] font-semibold text-gray-600 sm:text-xs">
                              {formatVolume(product.volume_m3)} m³
                            </p>
                            {(product.lourd || product.fragile) && (
                              <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-amber-700">
                                <ExclamationTriangleIcon className="h-3 w-3" />
                                {product.lourd ? "Lourd" : "Fragile"}
                              </span>
                            )}
                          </div>

                          <div className="mt-2 flex items-center justify-center gap-1">
                            <button
                              type="button"
                              disabled={quantity === 0}
                              aria-label={`Retirer un ${product.name}`}
                              className={`dw-press dw-focus flex h-8 w-8 items-center justify-center rounded-full border ${
                                quantity === 0
                                  ? "border-gray-100 bg-gray-50 text-gray-300"
                                  : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                              }`}
                              onClick={() =>
                                handleQuantityChange(product.name, -1)
                              }
                            >
                              <MinusIcon className="h-4 w-4" />
                            </button>
                            <span
                              className="min-w-[28px] text-center text-sm font-bold text-gray-900"
                              aria-live="polite"
                            >
                              {quantity}
                            </span>
                            <button
                              type="button"
                              aria-label={`Ajouter un ${product.name}`}
                              className="dw-accent-bg dw-press dw-focus flex h-8 w-8 items-center justify-center rounded-full shadow-sm"
                              onClick={() =>
                                handleQuantityChange(product.name, 1)
                              }
                            >
                              <PlusIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="col-span-full flex h-[240px] flex-col items-center justify-center gap-3 text-center text-gray-400">
                      <MagnifyingGlassIcon className="h-10 w-10 opacity-30" />
                      <div>
                        <p className="font-medium text-gray-600">
                          Aucun résultat pour « {searchTerm} »
                        </p>
                        <p className="text-sm">
                          Cet objet n'est pas au catalogue ? Ajoutez-le avec ses
                          dimensions.
                        </p>
                      </div>
                      <button
                        type="button"
                        className="dw-accent-bg dw-press dw-focus rounded-xl px-4 py-2 text-sm font-medium"
                        onClick={openCustomForm}
                      >
                        Ajouter cet objet manuellement
                      </button>
                    </div>
                  )}

                  {filteredProducts.length > 0 && (
                    <button
                      type="button"
                      onClick={openCustomForm}
                      className="dw-card dw-focus flex h-[205px] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-gray-300 bg-gray-50/60 p-3 text-gray-500 hover:border-gray-400 hover:bg-gray-50 sm:h-[230px] sm:p-4"
                    >
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm sm:h-14 sm:w-14">
                        <PlusIcon className="h-6 w-6" />
                      </span>
                      <span className="text-xs font-medium leading-tight sm:text-sm">
                        Objet non listé ?
                      </span>
                      <span className="px-1 text-center text-[10px] leading-tight text-gray-400">
                        Ajoutez-le avec ses dimensions
                      </span>
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {!showForm && (
            <div
              className="footer dw-surface-bg w-full border-t border-black/5 px-3 py-3 sm:px-4"
              style={{ backgroundColor: surfaceColor, color: onSurfaceColor }}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-1 flex-col gap-1.5">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="text-xs font-medium uppercase tracking-wide opacity-70">
                      Volume estimé
                    </span>
                    <span
                      className="text-2xl font-bold leading-none"
                      aria-live="polite"
                    >
                      {totalVolume.toFixed(2)} m³
                    </span>
                    <span className="text-xs opacity-70">
                      {itemCount} objet{itemCount > 1 ? "s" : ""}
                    </span>

                    {totalVolume > 0 && widgets.priceActive && (
                      <span className="flex items-center gap-1.5 text-sm font-semibold">
                        <span className="opacity-70">·</span>
                        {totalPrice.toFixed(2)} €
                        <Tooltip
                          content={`Prix du voyage vide : ${widgets.voyageVidePrice}€`}
                        >
                          <InformationCircleIcon className="h-4 w-4 opacity-60" />
                        </Tooltip>
                      </span>
                    )}
                  </div>

                  {truckHint && (
                    <div className="flex items-center gap-2">
                      <TruckIcon className="h-4 w-4 shrink-0 opacity-70" />
                      <span className="text-xs opacity-70">
                        équivaut à {truckHint.label}
                      </span>
                      <span className="dw-progress hidden h-1.5 w-28 overflow-hidden rounded-full sm:block">
                        <span
                          className="dw-progress__bar block h-full rounded-full"
                          style={{ width: `${truckFill}%` }}
                        />
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {itemCount > 0 && (
                    <button
                      type="button"
                      className="dw-press dw-focus hidden rounded-xl border border-black/10 bg-white/70 px-3 py-2.5 text-sm font-medium text-gray-700 sm:block"
                      onClick={() => setInventaire(!inventaire)}
                    >
                      {inventaire ? "Catalogue" : "Voir l'inventaire"}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={totalVolume <= 0}
                    className={`dw-press dw-focus flex w-full items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold sm:w-auto ${
                      totalVolume > 0
                        ? "dw-accent-bg shadow-sm"
                        : "cursor-not-allowed bg-black/10 text-gray-500"
                    }`}
                    title={
                      totalVolume > 0
                        ? undefined
                        : "Ajoutez au moins un objet à votre inventaire"
                    }
                    onClick={() => {
                      setShowForm(true);
                      setFormStep(0);
                    }}
                  >
                    Demander un devis
                    <FontAwesomeIcon icon={faCircleArrowRight} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default DemenageurWidget;
